import {isQuotaExceededError, safeLocalStorage} from '../util/localStorage';

const UNDEFINED_VALUE = '__undefined__';
const QUOTA_EXCEEDED_MESSAGE =
  'Unable to save to localStorage. Memory limit exceeded, even after freeing up space.';

export type SetResult = 'quotaExceeded' | 'saved' | 'skipped';

export class LocalStorageBackedLRU<T extends {} = {}> {
  constructor(
    private readonly maxBytesPerEntry: number = 1e6,
    private readonly prefix: string = 'wv_'
  ) {
    // LocalStorage is limited to 5MB, so we'll use 1MB as our default for the maxBytesPerEntry
  }

  public set(key: string, value: T): boolean {
    const result = this.trySet(key, value);
    if (result === 'quotaExceeded') {
      this.logQuotaExceeded();
    }
    return result === 'saved';
  }

  /** Saves without logging a quota failure, so a caller writing a batch can log it once. */
  public trySet(key: string, value: T): SetResult {
    this.del(key);
    const valStr =
      value === undefined ? UNDEFINED_VALUE : JSON.stringify(value);
    const size = valStr.length;
    if (size > this.maxBytesPerEntry) {
      return 'skipped';
    }

    while (true) {
      try {
        this.prefixedLocalStorageSetItem(key, valStr);
        return 'saved';
      } catch (e) {
        if (isQuotaExceededError(e)) {
          const removed = this.removeLeastRecentlyUsed();
          // Try to free up some space
          if (!removed) {
            return 'quotaExceeded';
          }
        } else {
          console.error('Unexpected error saving to localStorage', e);
          return 'skipped';
        }
      }
    }
  }

  public logQuotaExceeded(): void {
    console.error(QUOTA_EXCEEDED_MESSAGE);
  }

  public get(key: string): T | null | undefined {
    const valStr = this.prefixedLocalStorageGetItem(key);
    if (!valStr) {
      return null;
    }
    if (valStr === UNDEFINED_VALUE) {
      return undefined as any;
    }
    const value = JSON.parse(valStr);
    this.del(key);
    this.set(key, value);
    return value;
  }

  public del(key: string): void {
    const itemStr = this.prefixedLocalStorageGetItem(key);
    if (itemStr) {
      this.prefixedLocalStorageRemoveItem(key);
    }
  }

  public has(key: string): boolean {
    return this.prefixedLocalStorageGetItem(key) != null;
  }

  public reset(): void {
    while (this.removeLeastRecentlyUsed()) {
      // Keep removing until we can't anymore
    }
  }

  private removeLeastRecentlyUsed(): boolean {
    for (let i = 0; i < safeLocalStorage.length(); i++) {
      const prefixedKey = safeLocalStorage.key(i);
      if (prefixedKey.startsWith(this.prefix)) {
        // Only consider keys that start with our prefix
        safeLocalStorage.removeItem(prefixedKey);
        return true;
      }
    }
    return false;
  }

  private prefixedLocalStorageSetItem(key: string, value: string): void {
    safeLocalStorage.setItem(this.prefix + key, value);
  }

  private prefixedLocalStorageGetItem(key: string): string | null {
    return safeLocalStorage.getItem(this.prefix + key);
  }

  private prefixedLocalStorageRemoveItem(key: string): void {
    safeLocalStorage.removeItem(this.prefix + key);
  }
}
